import React, { useState, useEffect } from 'react';
import { X, Save, Edit, DollarSign, Calendar } from 'lucide-react';

const EditContributionModal = ({ isOpen, onClose, onSubmit, contribution, isLoading = false }) => {
  const [formData, setFormData] = useState({
    amount: '',
    period_month: new Date().getMonth() + 1,
    period_year: new Date().getFullYear(),
    status: 'pending',
    payment_method: 'cash',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  // Pré-remplir le formulaire avec les données de la contribution
  useEffect(() => {
    if (contribution && isOpen) {
      setFormData({
        amount: contribution.amount ? formatAmount(contribution.amount.toString()) : '',
        period_month: contribution.period_month || new Date().getMonth() + 1,
        period_year: contribution.period_year || new Date().getFullYear(),
        status: contribution.status || 'pending',
        payment_method: contribution.payment_method || 'cash',
        notes: contribution.notes || ''
      });
      setErrors({});
    }
  }, [contribution, isOpen]);

  const formatAmount = (value) => {
    // Supprimer tout ce qui n'est pas un chiffre
    const numbers = value.replace(/[^\d]/g, '');
    if (!numbers) return '';
    
    // Ajouter des espaces pour les milliers
    return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const handleAmountChange = (e) => {
    const formatted = formatAmount(e.target.value);
    setFormData(prev => ({
      ...prev,
      amount: formatted
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!formData.amount) newErrors.amount = 'Saisissez un montant';
    if (!formData.period_month) newErrors.period_month = 'Sélectionnez un mois';
    if (!formData.period_year) newErrors.period_year = 'Saisissez une année';

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // Convertir le montant en nombre
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount.replace(/\s/g, ''))
      };
      
      onSubmit(contribution.id, submitData);
    }
  };

  const resetForm = () => {
    setFormData({
      amount: '',
      period_month: new Date().getMonth() + 1,
      period_year: new Date().getFullYear(),
      status: 'pending',
      payment_method: 'cash',
      notes: ''
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen || !contribution) return null;

  const months = [
    { value: 1, label: 'Janvier' },
    { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' },
    { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Décembre' }
  ];

  const years = Array.from({length: 2030 - 2020 + 1}, (_, i) => 2020 + i);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Edit className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Modifier la Cotisation</h2>
              <p className="text-sm text-gray-500">
                {contribution.user?.first_name} {contribution.user?.last_name}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Montant */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <DollarSign className="inline h-4 w-4 mr-1" />
              Montant (F CFA)
            </label>
            <input
              type="text"
              value={formData.amount}
              onChange={handleAmountChange}
              placeholder="Ex: 50 000"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                errors.amount ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.amount && (
              <p className="mt-1 text-sm text-red-600">{errors.amount}</p>
            )}
          </div>

          {/* Période */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline h-4 w-4 mr-1" />
                Mois
              </label>
              <select
                value={formData.period_month}
                onChange={(e) => setFormData(prev => ({...prev, period_month: parseInt(e.target.value)}))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.period_month ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
              {errors.period_month && (
                <p className="mt-1 text-sm text-red-600">{errors.period_month}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Année
              </label>
              <select
                value={formData.period_year}
                onChange={(e) => setFormData(prev => ({...prev, period_year: parseInt(e.target.value)}))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.period_year ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                {years.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              {errors.period_year && (
                <p className="mt-1 text-sm text-red-600">{errors.period_year}</p>
              )}
            </div>
          </div>

          {/* Statut */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statut
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData(prev => ({...prev, status: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="pending">En attente</option>
              <option value="paid">Payé</option>
              <option value="overdue">En retard</option>
            </select>
          </div>

          {/* Méthode de paiement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Méthode de paiement
            </label>
            <select
              value={formData.payment_method}
              onChange={(e) => setFormData(prev => ({...prev, payment_method: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="cash">Espèces</option>
              <option value="bank_transfer">Virement bancaire</option>
              <option value="mobile_money">Mobile Money</option>
              <option value="check">Chèque</option>
              <option value="other">Autre</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optionnel)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
              placeholder="Commentaires ou informations supplémentaires..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditContributionModal;